import { useCallback, useMemo } from "react";
import type { OrganizationMember } from "@common/dto/organization/organization-member.dto";
import type { CloseOverviewMember } from "@common/schemas/clientsCloseOverview.schema";
import { OrganizationAction } from "@common/userOrganizationRoles";
import { useCurrentOrganizationMembers } from "@api/organizationMember.api";
import {
    OPEN_ROLE_FIELDS,
    UPDATE_WORK_ITEM_FAILED,
    type WorkItemEdit,
    type WorkRoleField,
    useUpdateWorkItem,
} from "@api/workItem.api";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import { getMemberLabel } from "@components/settings/OrganizationCloseTemplates/serviceTemplateScheduleMemberLabel";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { useScope } from "@state/scopeContext.lib";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";

/** The engagement team roles an edit can reassign, in the order they show. */
export const WORK_ROLES: ReadonlyArray<{
    field: WorkRoleField;
    label: string;
}> = [
    { field: "preparers", label: "Preparer" },
    { field: "reviewers", label: "Reviewer" },
    { field: "managers", label: "Manager" },
];

export type WorkStateRow = Pick<
    ClientsWorkOverviewWorkRow,
    "isFinished" | "cancelledAt" | "openRoles"
>;

type EditableWorkRow = WorkStateRow &
    Pick<ClientsWorkOverviewWorkRow, "closeId">;

/** Open work is neither done nor canceled; only open work can change. */
export const isOpenWork = (
    row: Pick<ClientsWorkOverviewWorkRow, "isFinished" | "cancelledAt">,
) => !row.isFinished && !row.cancelledAt;

/** Whether open work still has open tasks for a role to reassign. */
const isOpenWorkRole = (row: WorkStateRow, field: WorkRoleField) =>
    isOpenWork(row) && row.openRoles[OPEN_ROLE_FIELDS[field]].hasOpenTasks;

/** Whether open work still has open tasks for any role to reassign. */
export const hasOpenWorkRole = (row: WorkStateRow) =>
    WORK_ROLES.some(({ field }) => isOpenWorkRole(row, field));

/** Why a bulk edit or Cancel skips a row. */
export type WorkSkipReason = "doneOrCanceled" | "noOpenTasks";

/**
 * Why `edit` (or Cancel, without one) skips `row`, or null when it applies:
 * done and canceled work never changes, and a role edit needs open tasks
 * for that role.
 */
export const getWorkSkipReason = (
    row: WorkStateRow,
    edit?: WorkItemEdit,
): WorkSkipReason | null => {
    if (!isOpenWork(row)) {
        return "doneOrCanceled";
    }
    if (edit && "member" in edit && !isOpenWorkRole(row, edit.field)) {
        return "noOpenTasks";
    }
    return null;
};

/**
 * What the user can edit on a work item. Updating and canceling work needs
 * the organization settings permission (Admin and up), as the backend
 * checks; everyone else, and done or canceled work, sees plain values. A
 * role is editable only while it has open tasks to reassign, and the team
 * is while any role is.
 */
export const useWorkEditability = () => {
    const canEditWork = useCanPerformOrganizationAction(
        OrganizationAction.MANAGE_ORGANIZATION_SETTINGS,
    );
    return useMemo(() => {
        const canEditRow = (row: EditableWorkRow) =>
            canEditWork && !isBusyClosesDemoId(row.closeId);
        const isRoleEditable = (row: EditableWorkRow, field: WorkRoleField) =>
            canEditRow(row) && isOpenWorkRole(row, field);
        return {
            isItemEditable: (row: EditableWorkRow) =>
                canEditRow(row) && isOpenWork(row),
            isRoleEditable,
            isTeamEditable: (row: EditableWorkRow) =>
                canEditRow(row) && hasOpenWorkRole(row),
        };
    }, [canEditWork]);
};

export type WorkEditability = ReturnType<typeof useWorkEditability>;

/** A member's display name: their preferred name, else their email. */
export const getWorkMemberName = ({
    preferredName,
    email,
}: Pick<CloseOverviewMember, "preferredName" | "email">) =>
    preferredName ?? email;

/**
 * The organization members who can hold a role on work in every one of
 * `workspaceIds`, as a work item's team lists them (with their role on the
 * first client).
 */
export const getWorkTeamCandidates = ({
    members,
    workspaceIds,
    organizationId,
}: {
    members: OrganizationMember[];
    workspaceIds: string[];
    organizationId: string | null;
}): CloseOverviewMember[] =>
    members.flatMap((member) => {
        const assignments = workspaceIds.map((workspaceId) =>
            member.workspaceAssignments.find(
                (assignment) => assignment.workspaceId === workspaceId,
            ),
        );
        const [firstAssignment] = assignments;
        if (!firstAssignment || assignments.some((a) => a === undefined)) {
            return [];
        }
        return [
            {
                userId: member.user.id,
                role: firstAssignment.role,
                email: member.user.email ?? "",
                iconColor: member.user.iconColor,
                avatar: member.user.avatar,
                preferredName: getMemberLabel(member),
                organizationId,
            },
        ];
    });

/** The people who can hold a role on work for one client. */
export const useWorkTeamCandidates = (workspaceId: string) => {
    const { data: members = [] } = useCurrentOrganizationMembers({
        refetchOnMount: false,
    });
    const { organizationId = null } = useScope();
    return useMemo(
        () =>
            getWorkTeamCandidates({
                members,
                workspaceIds: [workspaceId],
                organizationId,
            }),
        [members, workspaceId, organizationId],
    );
};

/**
 * Saves one edit to a work item, toasting the reason when it fails, even when
 * the control that made the edit is gone by then.
 */
export const useEditWorkItem = () => {
    const { mutateAsync } = useUpdateWorkItem();
    const { error } = useToaster();
    return useCallback(
        (
            {
                closeId,
                workspaceId,
            }: Pick<ClientsWorkOverviewWorkRow, "closeId" | "workspaceId">,
            edit: WorkItemEdit,
        ) => {
            mutateAsync({ closeId, workspaceId, edit }).catch((reason) =>
                error(
                    reason instanceof Error
                        ? reason.message
                        : UPDATE_WORK_ITEM_FAILED,
                ),
            );
        },
        [mutateAsync, error],
    );
};
